import { html, TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { ItemReference } from '../../../../webmodule-common/cache/definitions/cache-item-reference';
import {
  WebmoduleChangeEvent,
  WebModuleLitTable,
  WebModuleLitTableColumnDef,
  WebmoduleRadioGroup,
  WebmoduleSelectEvent
} from '../../../../webmodule-common/components/src/webmodule-components';
import { when } from 'lit/directives/when.js';

import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter';
import { IStaleData } from '../../../../webmodule-common/other/stale-data';

import { ViewQuoteSummary, QuoteState, ResultGetQuoteSummary } from '../../../api/dealer-api-interface-quote';
import { resourceProject } from '../../../projects/ui/launcher';
import { getQuoteNumberFormatted, getQuoteStatus } from '../../../quotes/data/quote-helper-functions';
import { QuoteSummaryTable, QuoteSummaryTableOptions } from '../../../quotes/views/quote-list-view';
import { QuoteCacheData } from '../../cache-impl/cache-data';
import { cache } from '../../cache-impl/cache-registry';
import { ProjectResourceLink } from '../../cache-impl/resource-link';
import { staleIcon } from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../../webmodule-common/other/ui/modal-confirmation';
import { resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { emptyGuid } from '../../../../webmodule-common/other/api/empty-guid';
import { validId } from '../../../../webmodule-common/other/ui/string-helper-functions';

@customElement('wm-franchiseequotesummarytable')
export class FranchiseeQuoteSummaryTable extends QuoteSummaryTable {
  private projectCache = cache().project;
  private quoteCache = cache().quote;
  private linkCache = cache().projectResourceLink;
  private showAllQuotes = true;
  private count = 1;
  protected staleData: IStaleData;

  constructor(options: QuoteSummaryTableOptions) {
    super(options);
    //this blocks loading of any data until the first time we want it to occur.
    this.staleData = options.stale ?? { exists: () => false };
    this.sortAscending = false;
  }

  isDataLoadDelayed() {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getColumns(): WebModuleLitTableColumnDef[] {
    const columns: WebModuleLitTableColumnDef[] = [];
    columns.push({
      title: () => LangFranchiseeQuoteListView.quoteNumberTitle(),
      fieldName: 'quoteNumber',
      sortable: true,
      isDefaultSortDirectionAscending: false,
      classes: 'colpx-130 colpxmax-130 quote-number',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const quoteSummary = item as ViewQuoteSummary;
        return this.stale(quoteSummary)
          ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
            `${staleIcon(false)}${this.quoteLink(quoteSummary, getQuoteNumberFormatted(quoteSummary))}`
          : this.quoteLink(quoteSummary, getQuoteNumberFormatted(quoteSummary));
      }
    });

    columns.push(
      ...[
        {
          title: () => LangFranchiseeQuoteListView.quoteTitle(),
          fieldName: 'title',
          sortable: true,
          classes: 'colpx-400 quote-title',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.quoteLink(quoteSummary, quoteSummary.title);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.projectTitle(),
          fieldName: 'id',
          sortable: false,
          classes: 'colpx-270 colpxmax-270 quote-project-number',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getProjectLink(quoteSummary.id);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.clientTitle(),
          fieldName: 'quoteCustomerId',
          sortable: false,
          classes: 'colpx-240 colpxmax-240 quote-client-name',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getCustomerLink(quoteSummary.quoteCustomerId);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.authorTitle(),
          fieldName: 'assignedToUserId',
          sortable: false,
          classes: 'colpx-200 colpxmax-200 quote-created-by',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getUserDisplayValue(quoteSummary.assignedToUserId);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.lastModifiedTitle(),
          fieldName: 'lastModifiedDate',
          sortable: false,
          classes: 'colpx-180 colpxmax-180 quote-modified-date',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            const dt = new Date(quoteSummary.lastModifiedDate ?? '');
            return html`${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.statusTitle(),
          fieldName: 'state',
          sortable: false,
          classes: 'colpx-130 colpxmax-130 quote-status no-pseudo',

          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return getQuoteStatus(quoteSummary.state, true);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.netAmountTitle(),
          fieldName: 'calculatedNetTotal',
          sortable: false,
          classes: 'colpx-110 colpxmax-110 alignment-right quote-amount no-pseudo',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return moneyToTemplateResult(quoteSummary.calculatedNetTotal);
          }
        },
        {
          title: html` <webmodule-icon library="fa" name="fas-bars"></webmodule-icon>`,
          fieldName: 'id',
          sortable: false,
          classes: 'colpx-50 colpxmax-50 item-menu',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.ellipsisMenu(quoteSummary);
          }
        }
      ]
    );
    return columns;
  }

  stale(row: ViewQuoteSummary) {
    return this.staleData.exists(row.id);
  }

  override enableAdvancedFiltering(): boolean {
    return true;
  }

  override advancedFilterTemplate(): TemplateResult {
    this.count++;
    const createRadioButton = (text: string, input: boolean) => {
      return html` <webmodule-radio value="${input}">${text}</webmodule-radio>`;
    };

    return html`
      <webmodule-radio-group
        name="filtergroup${this.count.toString()}"
        class="quote-author-filter"
        value="${this.showAllQuotes}"
        inline
        size="small"
        @webmodule-change=${(e: WebmoduleChangeEvent) => this.onQuoteAuthorChanged(e)}
      >
        ${createRadioButton(LangFranchiseeQuoteListView.allQuotesRadio(), true)}
        ${createRadioButton(LangFranchiseeQuoteListView.myQuotesRadio(), false)}
      </webmodule-radio-group>
    `;
  }

  async onMenuItemSelected(event: WebmoduleSelectEvent, quoteSummary: ViewQuoteSummary) {
    if (event.detail.item.value == 'delete') {
      if (
        await AskConfirmation(
          LangFranchiseeQuoteListView.deleteQuoteConfirmation(),
          getConfirmationButtons()[ConfirmationButtonType.yesNo]
        )
      )
        await this.deleteQuoteItem(quoteSummary);
    } else if (event.detail.item.value == 'copy') {
      await this.copyQuoteItem(quoteSummary);
    }
  }

  ellipsisMenu(quoteSummary: ViewQuoteSummary): TemplateResult {
    return html` <webmodule-dropdown
      placement="bottom-end"
      hoist
      @webmodule-select=${(e: WebmoduleSelectEvent) => this.onMenuItemSelected(e, quoteSummary)}
    >
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        ${when(
          !(quoteSummary.state == QuoteState.Draft),
          () => '',
          () =>
            html` <webmodule-menu-item class="action-delete" value="delete">
              <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
              ${LangFranchiseeQuoteListView.deleteButton()}
            </webmodule-menu-item>`
        )}

        <webmodule-menu-item class="action-copy" value="copy">
          <webmodule-icon slot="prefix" library="fa" name="fas-copy"></webmodule-icon>
          ${LangFranchiseeQuoteListView.copyButton()}
        </webmodule-menu-item>
      </webmodule-menu>
    </webmodule-dropdown>`;
  }

  async rowClicked(e: CustomEvent<{ table: WebModuleLitTable; item: ViewQuoteSummary }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.eventHandler(async () => {});
  }

  protected getPreFetched(results: ResultGetQuoteSummary): Promise<void>[] {
    const cachePrefetch = super.getPreFetched(results);

    const updateFromSummaries: ((items: ViewQuoteSummary[]) => Promise<ItemReference<QuoteCacheData>[]>) | undefined = (
      this.quoteCache as any
    ).updateFromSummaries.bind(this.quoteCache);
    if (updateFromSummaries) {
      //this is the optimized efficient method
      const p = updateFromSummaries(results.quoteSummary.results).then(items => {
        const quoteIds = items.map(x => x.id);

        return this.linkCache.preFetch(quoteIds).then(() => {
          const projectIds: string[] = [];
          results.quoteSummary.results.forEach(summary => {
            const projectId = this.linkCache.getLocalData(summary.id)?.projectId ?? emptyGuid;
            if (validId(projectId)) projectIds.push(projectId);
          });
          return this.projectCache.preFetch(projectIds);
        });
      });

      cachePrefetch.push(p);
    } else {
      //less optimized fallback
      const quoteIds = results.quoteSummary.results.map(x => x.id);

      cachePrefetch.push(
        this.quoteCache
          .preFetch(quoteIds)
          .then(() => {
            return this.linkCache.getMany(quoteIds);
          })
          .then((data: ItemReference<ProjectResourceLink>[] | null) => {
            const projectIds = data?.map(x => x.data.projectId) ?? [];
            return this.projectCache.preFetch(projectIds);
          })
      );
    }
    return cachePrefetch;
  }

  protected async onQuoteAuthorChanged(e: WebmoduleChangeEvent) {
    const target = e.target as WebmoduleRadioGroup | null;

    if (target === null) return;

    const groupValue = target.value;

    if (groupValue === 'true') {
      this.showAllQuotes = true;
      this.assignedToUserId = null;
    } else {
      this.assignedToUserId = getCurrentUser()?.id ?? null;
      this.showAllQuotes = false;
    }

    await this.refreshData();
    this.requestUpdate();
  }

  afterReload() {
    this.dataIsStale = false;
  }

  protected getProjectLink(id: string) {
    if (id == emptyGuid) return '';

    const link = this.linkCache.getLocalData(id);

    if (!link) return '';

    const project = this.projectCache.getLocal(link.projectId);

    return project
      ? html`<a class="project-link" href="${resolveURL(resourceProject, project.id)}" data-projectid="${project.id}"
          >${project.data.projectSummary.title}</a
        >`
      : '';
  }
}
